import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Drives the live Connection page. Subscribes to ClientStatusChannel and:
//   * prepends new events to the activity feed (lap / ingest / heartbeat / …)
//   * refreshes the capture card stats and ingest rate
//   * keeps "Last heartbeat" / online indicators current
//   * ages feed timestamps in place on a timer
//   * flips the first-run screen to the connected monitor (reload) when the
//     first telemetry arrives
//
// Distinct from `client_status_controller` (the sidebar avatar dot) — this one
// owns the whole page.
export default class extends Controller {
  static targets = [
    "feed",
    "feedEmpty",
    "ingestRate",
    "lastLap",
    "bestLap",
    "delta",
    "lastHeartbeat",
    "onlineLabel",
    "statusChip",
    "streamPill",
  ]
  // connected: the account has a client at all (onboarding vs monitor).
  // live: a session is actively capturing right now (drives the speed readout,
  // streaming pill, animated trace — all server-rendered).
  static values = { connected: Boolean, live: Boolean }

  // Per-kind feed icon (path + accent) — mirrors ClientHelper::FEED_KINDS.
  KINDS = {
    lap: {
      color: "#A78BFA",
      path: "M3.75 13.5 14.25 3l-1.5 7.5h7.5L9.75 21l1.5-7.5h-7.5Z",
    },
    ingest: {
      color: "#00D4FF",
      path: "M9.348 14.652a3.75 3.75 0 0 1 0-5.304m5.304 0a3.75 3.75 0 0 1 0 5.304m-7.425 2.121a6.75 6.75 0 0 1 0-9.546m9.546 0a6.75 6.75 0 0 1 0 9.546M5.106 18.894c-3.808-3.807-3.808-9.98 0-13.788m13.788 0c3.808 3.807 3.808 9.98 0 13.788M12 12h.008v.008H12V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z",
    },
    heartbeat: {
      color: "#22C55E",
      path: "M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
    },
    snapshot: {
      color: "#FF7A1C",
      path: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z",
    },
    weather: {
      color: "#6BE7FF",
      path: "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99",
    },
  }

  MAX_ITEMS = 11

  connect() {
    this.ingestTimes = []
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      { channel: "ClientStatusChannel" },
      { received: (data) => this.received(data) },
    )
    this.offlineTimeout = null
    this.ageTimer = setInterval(() => this.ageFeed(), 5000)
  }

  disconnect() {
    if (this.ageTimer) clearInterval(this.ageTimer)
    this.clearOfflineTimeout()
    if (this.subscription) this.subscription.unsubscribe()
    if (this.consumer) this.consumer.disconnect()
  }

  received(data) {
    // First telemetry on the onboarding screen → render the connected monitor.
    if (!this.connectedValue) {
      window.location.reload()
      return
    }

    // Telemetry arriving while the page is rendered idle means a session just
    // started capturing. Reload so the live affordances (animated speed chart,
    // streaming pill, capture stats) render from real state rather than being
    // faked client-side.
    if (!this.liveValue && (data.type === "ingest" || data.type === "lap")) {
      window.location.reload()
      return
    }

    switch (data.type) {
      case "heartbeat":
        this.setOnline()
        this.resetOfflineTimeout()
        this.prepend({
          kind: "heartbeat",
          title: "Heartbeat received",
          meta: data.device,
        })
        break
      case "ingest":
        this.bumpIngestRate()
        this.prepend({
          kind: "ingest",
          title: "Telemetry uploaded",
          meta: data.size,
        })
        break
      case "lap":
        this.prepend({
          kind: "lap",
          title: `Lap ${data.number} completed`,
          meta: data.time,
          deltaMs: data.delta_ms,
        })
        if (data.time) this.setText(this.lastLapTarget, data.time)
        if (data.best) this.setText(this.bestLapTarget, data.best)
        if (data.delta != null) this.setText(this.deltaTarget, data.delta)
        break
    }
  }

  // ── feed ─────────────────────────────────────────────────────────────
  prepend(event) {
    if (!this.hasFeedTarget) return
    if (this.hasFeedEmptyTarget) this.feedEmptyTarget.remove()

    const node = this.buildEvent(event)
    this.feedTarget.prepend(node)

    // Demote the previously-fresh card and cap the list.
    const cards = this.feedTarget.children
    for (let i = 1; i < cards.length; i++) this.demote(cards[i])
    while (this.feedTarget.children.length > this.MAX_ITEMS) {
      this.feedTarget.lastElementChild.remove()
    }
  }

  buildEvent(event) {
    const spec = this.KINDS[event.kind] || this.KINDS.heartbeat
    const wrap = document.createElement("div")
    wrap.className = "relative flex gap-3 animate-feed-in"

    const deltaLabel =
      event.kind === "lap" && event.deltaMs != null
        ? this.deltaLabel(event.deltaMs)
        : null
    const deltaCls = event.deltaMs < 0 ? "text-live" : "text-app-text-muted"

    wrap.innerHTML = `
      <span class="relative z-10 mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-app-surface ring-1 ring-app-ring" style="color:${spec.color}">
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="${spec.path}" />
        </svg>
      </span>
      <div class="min-w-0 flex-1 rounded-lg px-3.5 py-2.5 ring-1 transition-colors bg-app-surface-hover ring-app-border-strong">
        <div class="flex items-center gap-2">
          <p class="text-sm font-medium text-app-text">${this.escape(event.title)}</p>
          ${deltaLabel ? `<span class="font-mono text-xs font-medium tabular-nums ${deltaCls}">${deltaLabel}</span>` : ""}
          <span class="ml-auto shrink-0 text-[11px] text-app-text-faint" data-feed-age data-feed-at="${new Date().toISOString()}">just now</span>
        </div>
        ${event.meta ? `<div class="mt-0.5 flex items-center gap-3"><p class="font-mono text-xs text-app-text-muted">${this.escape(event.meta)}</p></div>` : ""}
      </div>
    `
    return wrap
  }

  // Drop the "just-arrived" highlight from an aged card.
  demote(card) {
    card.classList.remove("animate-feed-in")
    const body = card.querySelector(".rounded-lg")
    if (body) {
      body.classList.remove("bg-app-surface-hover", "ring-app-border-strong")
      body.classList.add("bg-app-surface-alt", "ring-app-border-subtle")
    }
  }

  ageFeed() {
    this.element.querySelectorAll("[data-feed-age]").forEach((el) => {
      const at = el.getAttribute("data-feed-at")
      if (at) el.textContent = this.ageLabel(new Date(at))
    })
  }

  ageLabel(date) {
    const secs = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000))
    if (secs < 5) return "just now"
    if (secs < 60) return `${secs}s ago`
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
    return `${Math.floor(secs / 86400)}d ago`
  }

  deltaLabel(ms) {
    const secs = ms / 1000
    return `${secs >= 0 ? "+" : ""}${secs.toFixed(3)}`
  }

  // ── capture card / device ──────────────────────────────────────────────
  bumpIngestRate() {
    const now = Date.now()
    this.ingestTimes.push(now)
    this.ingestTimes = this.ingestTimes.filter((t) => now - t <= 10000)
    if (this.hasIngestRateTarget) {
      const rate = (this.ingestTimes.length / 10).toFixed(1)
      this.ingestRateTarget.textContent = `${rate}/s`
    }
  }

  setOnline() {
    if (this.hasLastHeartbeatTarget) {
      this.lastHeartbeatTarget.textContent = "just now"
      this.lastHeartbeatTarget.classList.remove("text-app-text-muted")
      this.lastHeartbeatTarget.classList.add("text-live")
    }
    // Flip the "This device" pill to Online.
    if (this.hasOnlineLabelTarget) {
      this.onlineLabelTarget.classList.remove("text-app-text-faint")
      this.onlineLabelTarget.classList.add("text-live")
      this.onlineLabelTarget.innerHTML =
        '<span class="block h-1.5 w-1.5 rounded-full bg-live"></span> Online'
    }
    // Flip the header chip from "Offline ·" to "Connected ·".
    if (this.hasStatusChipTarget) {
      const name =
        this.statusChipTarget.querySelector(".text-app-text")?.textContent || ""
      this.statusChipTarget.innerHTML =
        '<span class="relative inline-flex">' +
        '<span class="block h-1.5 w-1.5 rounded-full bg-live"></span>' +
        '<span class="absolute inset-0 h-1.5 w-1.5 rounded-full bg-live-pulse opacity-75 animate-ping"></span>' +
        "</span>" +
        '<span class="text-app-text-secondary">Connected · ' +
        `<span class="text-app-text">${this.escape(name)}</span></span>`
    }
  }

  resetOfflineTimeout() {
    this.clearOfflineTimeout()
    this.offlineTimeout = setTimeout(() => this.setStale(), 90000)
  }

  setStale() {
    if (this.hasLastHeartbeatTarget) {
      this.lastHeartbeatTarget.classList.remove("text-live")
      this.lastHeartbeatTarget.classList.add("text-app-text-muted")
    }
  }

  clearOfflineTimeout() {
    if (this.offlineTimeout) clearTimeout(this.offlineTimeout)
    this.offlineTimeout = null
  }

  // ── utils ────────────────────────────────────────────────────────────
  setText(el, text) {
    if (el) el.textContent = text
  }

  escape(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }
}
